<template>
  <div class="topbar">
    <div class="breadcrumb">
      <BbBtn
        v-if="isEditMode"
        size="x-small"
        type="icon"
        icon="chevron-left"
        icon-size="small"
        no-background
        @click="goBack"
      ></BbBtn>
      <TextBody i18n-key="glossaries.glossaries" tag="span" />
      <span v-if="isEditMode">/ {{ name }}</span>
      <ActionsMenu
        v-if="isEditMode"
        v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.UPDATE }"
        :is-edit-mode="isEditMode"
        :id="glossary.id"
        @remove="onRemoveGlossary"
        @edit="isNewGlossaryPopupOpened = true"
      />
    </div>
    <!-- BbSwitch sets its width inline (100% by default), so it has to come through the prop -->
    <BbSwitch
      v-if="isEditMode"
      v-model="publicValue"
      :items="publicValues"
      width="211px"
      class="switch-public-mode"
    ></BbSwitch>
    <div class="actions">
      <BbBtn
        v-if="isEditMode"
        v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.UPDATE }"
        size="small"
        type="primary"
        color="#6222e3"
        @click="isUploadCsvPopupOpened = true"
      >
        <TextBody i18n-key="common.uploadCSV" />
      </BbBtn>
      <BbBtn
        v-if="isEditMode"
        v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.UPDATE }"
        size="small"
        type="primary"
        color="#6222e3"
        @click="emit('download-csv')"
      >
        <TextBody i18n-key="common.downloadCSV" />
      </BbBtn>
      <!-- a disabled button gets no hover, so the tooltip listens on the wrapper -->
      <BbTooltip
        v-if="isEditMode"
        v-bind="{
          placement: 'bottom',
          offset: 4,
          isDisabled: !isDisabled,
          content: t('glossaries.saveNeedsTwoLanguages'),
        }"
      >
        <span v-has-access:hide="{ resource: RESOURCE_TYPE.GLOSSARIES, resource_action: RESOURCE_ACTION_TYPE.UPDATE }">
          <BbBtn :is-disabled="isButtonDisabled" size="small" type="primary" color="#6222e3" @click="save">
            <TextBody i18n-key="common.save" />
          </BbBtn>
        </span>
      </BbTooltip>
      <BbToastMessage :modelValue="showSaveToast" :title="toastTitle" location="top right" />
      <BbBtn
        size="x-small"
        type="icon"
        icon="x-small"
        icon-size="medium"
        no-background
        :is-disabled="isSaving"
        @click="onShowGlossaryWarningModal"
      ></BbBtn>
    </div>
  </div>
  <GlossaryCsvUploadPopup
    :popup-opened="isUploadCsvPopupOpened"
    @update:open="isUploadCsvPopupOpened = $event"
    @execute:action="onCsvUpload"
  />
  <GlossarySavePopup
    :popup-opened="isNewGlossaryPopupOpened"
    :glossary-title="glossary.title"
    :is-edit-mode="isEditMode"
    @update:open="isNewGlossaryPopupOpened = false"
    @execute:action="editGlossary"
  />
  <WarningPopup
    :popup-opened="confirmCloseWithoutSavePopupOpened"
    title-key="modals.saveChanges"
    warning-message-key="modals.warningExitNoSaveMessage"
    confirm-text-key="modals.confirmText"
    @update:open="confirmCloseWithoutSavePopupOpened = false"
    @execute:action="onGlossaryClose"
  />
</template>

<script>
import { computed, ref, watch } from 'vue';
import { isEqual } from 'lodash';
import { useI18n } from 'vue-i18n';
import {
  GLOSSARY_SAVE_STATUS,
  GLOSSARY_SAVE_TOAST_DURATION,
  GLOSSARY_SAVE_TOAST_TITLE_KEYS,
} from '~/constants/glossaries';
import useGlossaries from '~/composables/useGlossaries';
import useModals from '~/composables/useModals';
import { RESOURCE_TYPE, RESOURCE_ACTION_TYPE } from '~/constants/user';
import TextBody from '~/components/common/TextBody';
import ActionsMenu from '~/components/common/ActionsMenu';
import GlossaryCsvUploadPopup from '~/components/glossary/GlossaryCsvUploadPopup';
import GlossarySavePopup from '~/components/glossary/GlossarySavePopup';
import WarningPopup from '~/components/common/WarningPopup.vue';
import { mapEntityEntriesToTableEntries } from '~/utils/glossary';

export default {
  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },
    currentRows: {
      type: Array,
      default: () => [],
    },
    currentHeaders: {
      type: Array,
      default: () => [],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    glossary: {
      type: Object,
      default: () => ({}),
    },
    saveStatus: {
      type: String,
      default: null,
    },
  },
  components: { WarningPopup, ActionsMenu, TextBody, GlossarySavePopup, GlossaryCsvUploadPopup },
  emits: ['back', 'save', 'remove-glossary', 'download-csv', 'upload-csv'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const publicValues = [
      { title: t('glossaries.private'), value: false },
      {
        title: t('glossaries.public'),
        value: true,
      },
    ];

    const { removeGlossary } = useGlossaries();
    const { setGlossaryModal } = useModals();

    // a new glossary has no isPublic yet and is saved as private
    const toPublicValue = (glossary) => Number(Boolean(glossary.isPublic));

    const publicValue = ref(toPublicValue(props.glossary));
    const name = ref(props.glossary.title);
    const isNewGlossaryPopupOpened = ref(false);
    const confirmCloseWithoutSavePopupOpened = ref(false);
    const showSaveToast = ref(false);
    const isUploadCsvPopupOpened = ref(false);
    let hideSaveToastTimeout;

    const onCsvUpload = (file) => {
      isUploadCsvPopupOpened.value = false;
      emit('upload-csv', file);
    };

    const isSaving = computed(() => props.saveStatus === GLOSSARY_SAVE_STATUS.SAVING);

    const isButtonDisabled = computed(() => props.isDisabled || isSaving.value);

    const toastTitle = computed(() => (props.saveStatus ? t(GLOSSARY_SAVE_TOAST_TITLE_KEYS[props.saveStatus]) : ''));

    watch(
      () => props.saveStatus,
      (status) => {
        clearTimeout(hideSaveToastTimeout);
        showSaveToast.value = Boolean(status);
        if (status && status !== GLOSSARY_SAVE_STATUS.SAVING) {
          hideSaveToastTimeout = setTimeout(() => {
            showSaveToast.value = false;
          }, GLOSSARY_SAVE_TOAST_DURATION);
        }
      },
    );

    const onShowGlossaryWarningModal = () => {
      const { headers: headersValue, rows: rowsValue } = mapEntityEntriesToTableEntries(props.glossary.entries);

      if (
        props.isEditMode &&
        (!isEqual(headersValue, props.currentHeaders) ||
          !isEqual(rowsValue, props.currentRows) ||
          publicValue.value !== toPublicValue(props.glossary))
      ) {
        confirmCloseWithoutSavePopupOpened.value = true;
      } else {
        onGlossaryClose();
      }
    };

    const onGlossaryClose = () => {
      setGlossaryModal(false);
      confirmCloseWithoutSavePopupOpened.value = false;
    };
    const goBack = () => {
      emit('back');
    };

    const onRemoveGlossary = async (id) => {
      await removeGlossary(id);
      emit('back');
    };

    const save = () => {
      emit('save', publicValue.value, name.value);
    };

    const editGlossary = (glossary) => {
      name.value = glossary.title || '';
      isNewGlossaryPopupOpened.value = false;
    };

    return {
      isUploadCsvPopupOpened,
      editGlossary,
      emit,
      onCsvUpload,
      isButtonDisabled,
      isSaving,
      isNewGlossaryPopupOpened,
      goBack,
      name,
      publicValue,
      publicValues,
      RESOURCE_ACTION_TYPE,
      RESOURCE_TYPE,
      onGlossaryClose,
      onRemoveGlossary,
      save,
      t,
      confirmCloseWithoutSavePopupOpened,
      onShowGlossaryWarningModal,
      toastTitle,
      showSaveToast,
    };
  },
};
</script>

<style scoped lang="scss">
.topbar {
  padding: $bb-spacing-3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: $bb-neutral-0;
  border-bottom: 1px solid $bb-neutral-4;

  .actions {
    gap: $bb-spacing-3;
    display: flex;
    align-items: center;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    min-width: 0;

    button {
      padding: 0;
    }

    span {
      @include bb-regular-text-styles('sm');
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-width: 100%;
      font-weight: 500;
    }
  }

  .switch-public-mode {
    flex-shrink: 0;

    :deep(.switch-item) {
      background-color: $bb-neutral-0 !important;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab--selected) {
      background-color: $bb-neutral-1;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab) {
      &:active {
        background-color: $bb-neutral-1 !important;
      }

      &:hover {
        background-color: $bb-neutral-3 !important;
      }
    }
  }
}
</style>
